// src/pages/OnboardingRecommendationPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// §7 (affichage de l'analyse) + §8 (proposition d'accompagnement).
// Lit la synthèse du batch (onboarding_deposits.summary), écrite par l'edge
// function analyze-deposit. L'analyse étant asynchrone, on poll jusqu'à
// `analyzed`. La recommandation est TRANSPARENTE : la phrase est composée CÔTÉ
// CLIENT à partir de `recommended_tier` + des compteurs, via i18n (suit la langue).
//
// Express (99 €) / Premium (399 €) → paiement unique via support_imports +
// create_import_checkout (prix fixé serveur). Conciergerie / Migration →
// « Contactez-nous ».
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import AiDisclosure from "@/components/AiDisclosure";
import { metierHomeRoute } from "../utils/onboardingProfile";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const POLL_MS = 2500;
const POLL_MAX = 20; // ~50 s

export default function OnboardingRecommendationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const depositId = params.get("d");
  const { user } = useAuth();

  const [deposit, setDeposit] = useState(null);
  const [polls, setPolls] = useState(0);
  const [payBusy, setPayBusy] = useState("");
  const [error, setError] = useState("");

  const fetchDeposit = useCallback(async () => {
    if (!depositId) return null;
    const { data, error: e } = await supabase
      .from("onboarding_deposits").select("*").eq("id", depositId).maybeSingle();
    if (e) { console.warn("[reco] fetch:", e.message); return null; }
    setDeposit(data);
    return data;
  }, [depositId]);

  // Pilote de l'analyse : relance analyze-deposit lot par lot (chunks) jusqu'à
  // done=true. Auto-réparant (si le 1er lancement a échoué) et scalable (chaque
  // appel reste court, sous le timeout des edge functions).
  useEffect(() => {
    if (!depositId) { navigate("/my-collection", { replace: true }); return; }
    let cancelled = false;
    (async () => {
      let dep = await fetchDeposit();
      let attempts = 0;
      while (!cancelled && attempts < POLL_MAX && dep && !["analyzed", "error"].includes(dep.status)) {
        attempts += 1;
        setPolls(attempts);
        try {
          const { data } = await supabase.functions.invoke("analyze-deposit", { body: { deposit_id: depositId } });
          if (!cancelled && data?.summary) {
            setDeposit((d) => ({ ...(d || {}), summary: data.summary, status: data.done ? "analyzed" : "analyzing" }));
          }
          if (data?.done) break;
        } catch (e) {
          await new Promise((r) => setTimeout(r, POLL_MS)); // relance au tour suivant
        }
        dep = await fetchDeposit();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositId]);

  const analyzing = deposit && !["analyzed", "error"].includes(deposit.status);

  const summary = deposit?.summary || {};
  const profileType = deposit?.profile_type || "collectionneur";
  const recommended = summary.recommended_tier || null;

  const metierLabel = useMemo(
    () => t(`onboarding.reco.metier.${profileType}`, { defaultValue: t("onboarding.reco.metier.generic") }),
    [t, profileType]
  );

  // Phrase de reco composée client-side → suit la langue (le champ summary.reason
  // écrit par la function, en FR, est volontairement ignoré ici).
  const reasonText = recommended
    ? t(`onboarding.reco.reason.${recommended}`, {
        documents: summary.documents || 0,
        aStructurer: summary.a_structurer || 0,
        defaultValue: "",
      })
    : "";

  const startPayment = async (tier) => {
    setPayBusy(tier);
    setError("");
    try {
      const { data: si, error: insErr } = await supabase
        .from("support_imports")
        .insert({
          user_id: user?.id || null,
          requester_email: user?.email || null,
          collector_email: user?.email || null,
          tier,
          metier: profileType,
          recommended_tier: recommended,
          deposit_id: depositId,
          status: "draft",
        })
        .select("id")
        .single();
      if (insErr) throw insErr;

      const { data, error: fxErr } = await supabase.functions.invoke("create_import_checkout", {
        body: { support_import_id: si.id },
      });
      if (fxErr) throw fxErr;
      if (!data?.sessionId) throw new Error(t("onboarding.reco.payError"));

      const stripe = await stripePromise;
      const { error: stErr } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      if (stErr) throw stErr;
    } catch (e) {
      console.error("[reco] payment:", e);
      setError(e.message || t("onboarding.reco.payError"));
      setPayBusy("");
    }
  };

  const continueAlone = () => window.location.assign(metierHomeRoute(profileType));

  if (!deposit) {
    return <Shell><p style={p}>{t("onboarding.reco.loading")}</p></Shell>;
  }

  if (analyzing) {
    return (
      <Shell>
        <p style={eyebrow}>{t("onboarding.reco.analyzingEyebrow")}</p>
        <h1 style={h1}>{t("onboarding.reco.analyzingTitle")}</h1>
        <div style={{ margin: "1.25rem 0" }}><div style={spinner} /></div>
        {summary.total > 0 && (
          <div style={{ margin: "0 0 1rem" }}>
            <div style={progressTrack}>
              <div style={{ ...progressFill, width: `${Math.round(((summary.analyzed_count || 0) / summary.total) * 100)}%` }} />
            </div>
            <p style={{ fontSize: "0.85rem", color: "#6b6459", marginTop: "0.5rem" }}>
              {t("onboarding.reco.analyzingProgress", { done: summary.analyzed_count || 0, total: summary.total })}
            </p>
          </div>
        )}
        <p style={p}>{t("onboarding.reco.analyzingBody")}</p>
        {polls >= POLL_MAX && (
          <p style={{ fontSize: "0.85rem", color: "#8a8378" }}>
            {t("onboarding.reco.tooLong")}{" "}
            <button style={linkBtn} onClick={continueAlone}>{t("onboarding.reco.continueAnyway")}</button>
          </p>
        )}
      </Shell>
    );
  }

  const counts = [
    { key: "documents", value: summary.documents || 0 },
    { key: "oeuvres", value: summary.oeuvres || 0 },
    { key: "certificats", value: summary.certificats || 0 },
    { key: "factures", value: summary.factures || 0 },
    { key: "aStructurer", value: summary.a_structurer || 0, warn: true },
  ];

  return (
    <Shell>
      <p style={eyebrow}>{t("onboarding.reco.resultEyebrow", { metier: metierLabel })}</p>
      <h1 style={h1}>{t("onboarding.reco.resultTitle")}</h1>
      <AiDisclosure
        type="assisted"
        style={{ marginTop: 8, marginBottom: 4 }}
        note={t("onboarding.reco.aiNote", { defaultValue: "Analyse de vos dépôts assistée par IA, à titre indicatif. Vos œuvres restent à compléter et vérifier dans votre espace." })}
      />

      {summary.consent_required && (
        <div style={consentBanner}>🔒 {t("onboarding.reco.consentRequired")}</div>
      )}

      <div style={countsGrid}>
        {counts.map((c) => (
          <div key={c.key} style={{ ...countCard, ...(c.warn && c.value > 0 ? countWarn : {}) }}>
            <div style={{ fontSize: "1.6rem", fontWeight: 700, color: c.warn && c.value > 0 ? "#b45309" : "#1a1a1a" }}>
              {c.warn && c.value > 0 ? "⚠ " : "✓ "}{c.value}
            </div>
            <div style={{ fontSize: "0.78rem", color: "#6b6459" }}>{t(`onboarding.reco.counts.${c.key}`)}</div>
          </div>
        ))}
      </div>

      {deposit.status === "error" && <p style={{ ...p, color: "#b42318" }}>{t("onboarding.reco.errorBody")}</p>}

      <p style={{ ...eyebrow, marginTop: "1.75rem" }}>{t("onboarding.reco.howContinue")}</p>
      {reasonText && <p style={recoReason}>💡 {reasonText}</p>}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.75rem" }}>
        <OfferCard
          title={t("onboarding.reco.offers.autonomous.title")}
          price={t("onboarding.reco.offers.autonomous.price")}
          desc={t("onboarding.reco.offers.autonomous.desc")}
          onClick={continueAlone}
          recommended={recommended === "autonome"}
          recLabel={t("onboarding.reco.recommended")}
        />
        <OfferCard
          title={t("onboarding.reco.offers.express.title")}
          price={t("onboarding.reco.offers.express.price")}
          desc={t("onboarding.reco.offers.express.desc")}
          onClick={() => startPayment("express")}
          busy={payBusy === "express"}
          busyLabel={t("onboarding.reco.payRedirect")}
          recommended={recommended === "express"}
          recLabel={t("onboarding.reco.recommended")}
        />
        <OfferCard
          title={t("onboarding.reco.offers.premium.title")}
          price={t("onboarding.reco.offers.premium.price")}
          desc={t(`onboarding.reco.offersByMetier.${profileType}.premiumDesc`, { defaultValue: t("onboarding.reco.offers.premium.desc") })}
          onClick={() => startPayment("premium")}
          busy={payBusy === "premium"}
          busyLabel={t("onboarding.reco.payRedirect")}
          recommended={recommended === "premium"}
          recLabel={t("onboarding.reco.recommended")}
          star
        />
        <OfferCard
          title={t(`onboarding.reco.offersByMetier.${profileType}.top.title`, { defaultValue: t("onboarding.reco.offers.concierge.title") })}
          price={t(`onboarding.reco.offersByMetier.${profileType}.top.price`, { defaultValue: t("onboarding.reco.offers.concierge.price") })}
          desc={t(`onboarding.reco.offersByMetier.${profileType}.top.desc`, { defaultValue: t("onboarding.reco.offers.concierge.desc") })}
          as={Link}
          to="/contact"
          muted
        />
      </div>

      {error && <p style={errorStyle}>{error}</p>}
    </Shell>
  );
}

function OfferCard({ title, price, desc, onClick, busy, busyLabel, recommended, recLabel, star, muted, as: As, to }) {
  const inner = (
    <div style={{ ...offer, ...(recommended ? offerReco : {}), ...(muted ? offerMuted : {}) }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
        <span style={{ fontWeight: 700, color: "#1a1a1a" }}>
          {star ? "⭐ " : ""}{title}
          {recommended && <span style={recoBadge}>{recLabel}</span>}
        </span>
        <span style={{ fontWeight: 700, color: "#1a1a1a", flexShrink: 0 }}>{price}</span>
      </div>
      <p style={{ fontSize: "0.85rem", color: "#6b6459", margin: "0.35rem 0 0" }}>{busy ? busyLabel : desc}</p>
    </div>
  );
  if (As) return <As to={to} style={{ textDecoration: "none" }}>{inner}</As>;
  return (
    <button onClick={onClick} disabled={busy} style={{ all: "unset", cursor: busy ? "default" : "pointer", display: "block" }}>
      {inner}
    </button>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#f9f7f4,#f1ede6)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2.5rem 1.25rem", fontFamily: "Georgia, serif" }}>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e0d8", borderRadius: 18, padding: "2.5rem" }}>
        {children}
      </div>
    </div>
  );
}

const eyebrow = { fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#c9a961", margin: 0 };
const h1 = { fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.2, color: "#1a1a1a", margin: "0.6rem 0 1.25rem" };
const p = { fontSize: "0.95rem", lineHeight: 1.6, color: "#4a453d", margin: "0 0 1rem" };
const countsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: "0.6rem" };
const countCard = { background: "#f9f7f4", border: "1px solid #e5e0d8", borderRadius: 10, padding: "0.85rem 0.5rem", textAlign: "center" };
const countWarn = { background: "#fef6ec", borderColor: "#f3d9ac" };
const recoReason = { background: "#eef5f1", border: "1px solid #cfe3d8", borderRadius: 10, padding: "0.75rem 0.9rem", fontSize: "0.88rem", color: "#2e7d55", margin: "0.4rem 0 0" };
const offer = { background: "#fff", border: "1px solid #e5e0d8", borderRadius: 12, padding: "1rem 1.15rem" };
const offerReco = { borderColor: "#c9a961", boxShadow: "0 0 0 2px #c9a96133" };
const offerMuted = { background: "#faf8f5" };
const recoBadge = { fontSize: "0.62rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#fff", background: "#c9a961", borderRadius: 999, padding: "0.15rem 0.5rem", marginLeft: "0.5rem" };
const errorStyle = { color: "#b42318", fontSize: "0.85rem", margin: "0.75rem 0 0" };
const consentBanner = { background: "#fef6ec", border: "1px solid #f3d9ac", borderRadius: 10, padding: "0.75rem 0.9rem", fontSize: "0.85rem", color: "#8a5a10", lineHeight: 1.5, margin: "0 0 1rem" };
const linkBtn = { all: "unset", color: "#1a1a1a", textDecoration: "underline", cursor: "pointer" };
const spinner = { width: 34, height: 34, borderRadius: "50%", border: "3px solid #e5e0d8", borderTopColor: "#c9a961", animation: "spin 0.9s linear infinite" };
const progressTrack = { width: "100%", height: 6, background: "#eee8df", borderRadius: 999, overflow: "hidden" };
const progressFill = { height: "100%", background: "#c9a961", borderRadius: 999, transition: "width 0.3s ease" };
